import { CommonModule } from '@angular/common'
import { Component, DestroyRef, inject } from '@angular/core'
import { computed, effect, input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { ReactiveFormsModule } from '@angular/forms'

import { Store } from '@ngrx/store'
import { SelectModule } from 'primeng/select'

import { distinctUntilChanged } from 'rxjs'

import { FlxFormControlModule } from '@flowx/angular-ui-toolkit'
import {
  ConversationThreadContextDataItemType,
  ConversationThreadExecutionPlanActionState,
  ActionTarget,
  ConversationThreadExecutionPlanActionTargetType,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { ConversationThreadExecutionPlanActionTargetExpressionType } from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import {
  ContextDataKeys,
  TargetNodeType,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { AiExecutionPlanService } from '@flowx/designer/modules/ai/conversation/ai-execution-plan.service'
import { ConversationActions } from '@flowx/designer/modules/ai/conversation/conversation.actions'
import { ExecutionPlanActions } from '@flowx/designer/modules/ai/conversation/execution-plan.actions'
import { ChatMessage } from '@flowx/designer/modules/ai/types/ai.types'
import { NodeDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { ActionHandler } from '@flowx/designer/modules/node-actions/components/action-edit/action-edit.dictionary'
import {
  AppNavigationService,
  NavigationActions,
} from '@flowx/designer/shared/app-navigation/app-navigation.service'
import { ActivityNode } from '@flowx/designer/shared/entities/node.types'
import {
  componentsWithDisable,
  componentsWithHide,
} from '@flowx/designer/shared/entities/template-config.entity'
import * as NodeActionsSelectors from '@flowx/designer/shared/store/selectors/node-actions.selectors'
import { nodesSelector } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  selectAppId,
  selectAppVersionId,
  selectProcessResourceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'
import {
  selectTemplateConfig,
  selectedTemplateConfig,
  selectedTemplateConfigIds,
} from '@flowx/designer/shared/store/selectors/template-config.selectors'

@Component({
  selector: 'flx-ai-destination-selector',
  templateUrl: './ai-destination-selector.component.html',
  imports: [CommonModule, ReactiveFormsModule, SelectModule, FlxFormControlModule],
})
export class AiDestinationSelectorComponent {
  #store = inject(Store)
  #appNavigationService = inject(AppNavigationService)
  #destroyRef = inject(DestroyRef)
  #aiExecutionPlanService = inject(AiExecutionPlanService)
  message = input.required<ChatMessage>()
  nodes = this.#store.selectSignal(nodesSelector)
  ActivityNode = ActivityNode

  destinationTasks = computed(() =>
    this.nodes().filter((node) =>
      [ActivityNode.USER_TASK, ActivityNode.TASK, ActivityNode.MESSAGE_EVENT_SEND].includes(
        node.type as ActivityNode
      )
    )
  )

  uiDestinationTasks = computed(() =>
    this.destinationTasks().filter((node) => node.type === ActivityNode.USER_TASK)
  )

  destinationForm = new FormGroup({
    nodeId: new FormControl(null, [Validators.required]),
    actionId: new FormControl(-1),
    businessRuleIndex: new FormControl(-1),
    templateConfigId: new FormControl({ value: null, disabled: true }),
    expressionType: new FormControl(null),
  })
  destinationFormValue = toSignal(this.destinationForm.valueChanges)

  selectedNodeId = signal<string | number | null>(null)
  selectedActionId = signal<string | number | null>(null)
  implementHidden = signal(true)

  actions = computed(() => {
    const nodeId = this.selectedNodeId()
    const actions = this.#store.selectSignal(NodeActionsSelectors.nodeActionsById(nodeId))()

    return [
      {
        id: -1,
        name: 'New Action',
      },
      ...(actions?.filter((action) => action.handlerService === ActionHandler.NO_OP) || []),
    ]
  })

  businessRules = computed(() => {
    const actionId = this.selectedActionId()

    const businessRules = this.actions()?.find(
      (action) => action.id === actionId
    )?.actionRuleDefinitions

    const sortedBusinessRules = [...(businessRules || [])]
      .sort((a, b) => a.order - b.order)
      .map((rule) => ({
        label: 'Rule ' + ((rule.order || 0) + 1),
        ...rule,
        id: rule.order || 0,
      }))

    return [
      ...(sortedBusinessRules.length === 0 && this.selectedActionId() === -1
        ? [{ id: 0, label: 'Rule 1' }]
        : [
            {
              id: -1,
              label: 'New Business Rule',
            },
          ]),
      ...sortedBusinessRules,
    ]
  })

  destinationSelector = computed(() => {
    const message = this.message()

    return message.destinationSelector
  })

  isBusinessRuleDestinationRequired = computed(() => {
    const destinationSelector = this.destinationSelector()

    return (
      destinationSelector?.agent === AgentName.AiDeveloper &&
      [AgentAction.BusinessRule, AgentAction.EditBusinessRule].includes(destinationSelector?.action)
    )
  })

  isExpressionDestinationRequired = computed(() => {
    const destinationSelector = this.destinationSelector()

    return (
      destinationSelector?.agent === AgentName.AiDeveloper &&
      destinationSelector?.action === AgentAction.Expression
    )
  })

  isUiDestinationRequired = computed(() => {
    const destinationSelector = this.destinationSelector()

    return destinationSelector?.agent === AgentName.AiDesigner
  })

  selectedTemplateConfig = this.#store.selectSignal(selectedTemplateConfig)
  selectedTemplateConfigIds = this.#store.selectSignal(selectedTemplateConfigIds)

  templateConfigs = signal<{ id: number; label: string }[]>([])
  expressionTypes = [
    {
      id: ConversationThreadExecutionPlanActionTargetExpressionType.Hide,
      label: 'Hide',
    },
    {
      id: ConversationThreadExecutionPlanActionTargetExpressionType.Disable,
      label: 'Disable',
    },
  ]
  appId = this.#store.selectSignal(selectAppId)()
  appVersionId = this.#store.selectSignal(selectAppVersionId)()
  selectedProcessResourceId = this.#store.selectSignal(selectProcessResourceId)()

  showExpressionError = computed(() => {
    if (!this.isExpressionDestinationRequired()) {
      return false
    }

    const templateType = this.selectedTemplateConfig()?.componentIdentifier
    const expressionType = this.destinationFormValue()?.expressionType
    if (expressionType === 'HIDE') {
      return !componentsWithHide.includes(templateType)
    }
    if (expressionType === 'DISABLE') {
      return !componentsWithDisable.includes(templateType)
    }

    return false
  })

  implementButtonLabel = computed(() => {
    if (this.isExpressionDestinationRequired()) {
      return 'Implement on Selected Component'
    }

    return 'Implement'
  })
  nodeTemplateConfigs = this.#store.selectSignal(selectTemplateConfig)
  constructor() {
    this.destinationForm
      .get('nodeId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef), distinctUntilChanged())
      .subscribe((value) => {
        this.selectedNodeId.set(value || null)
        this.selectedActionId.set(-1)
        this.destinationForm.get('actionId')?.setValue(-1)
        this.destinationForm.get('businessRuleIndex')?.setValue(0)
        this.destinationForm.get('templateConfigId')?.setValue(null)
        if (this.isExpressionDestinationRequired()) {
          this.#navigateToNode(this.nodes().find((node) => node.id === value))
        }
      })

    this.destinationForm
      .get('actionId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value) => {
        this.selectedActionId.set(value || null)
        this.destinationForm.get('businessRuleIndex')?.setValue(value === -1 ? 0 : -1)
      })

    this.destinationForm
      .get('templateConfigId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value) => {
        if (!this.isExpressionDestinationRequired()) {
          return
        }

        if (value) {
          this.destinationForm
            .get('nodeId')
            ?.setValue(
              this.nodes().find(
                (node) => node.navigationAreaId === this.selectedTemplateConfig()?.navigationAreaId
              )?.id,
              { emitEvent: false }
            )
        }
      })

    effect(() => {
      if (!this.isExpressionDestinationRequired()) {
        return
      }

      const selectedTemplateConfig = this.selectedTemplateConfig()

      if (!selectedTemplateConfig) {
        this.destinationForm.get('templateConfigId')?.setValue(null)
        this.destinationForm.get('nodeId')?.setValue(null)

        return
      }

      this.templateConfigs.set([
        {
          id: selectedTemplateConfig?.id,
          label:
            selectedTemplateConfig?.customIdentifier || selectedTemplateConfig?.componentIdentifier,
        },
      ])
      this.destinationForm.get('templateConfigId')?.setValue(this.selectedTemplateConfig()?.id)
    })

    effect(() => {
      this.destinationForm.get('expressionType')?.clearValidators()
      this.destinationForm.get('templateConfigId')?.clearValidators()
      this.destinationForm.get('actionId')?.clearValidators()
      this.destinationForm.get('businessRuleIndex')?.clearValidators()

      if (this.isExpressionDestinationRequired()) {
        this.destinationForm.get('expressionType')?.setValidators([Validators.required])
        this.destinationForm.get('templateConfigId')?.setValidators([Validators.required])
        return
      }

      if (this.isBusinessRuleDestinationRequired()) {
        this.destinationForm.get('actionId')?.setValidators([Validators.required])
        this.destinationForm.get('businessRuleIndex')?.setValidators([Validators.required])
        return
      }
    })

    // Initialize form with nodeId from partial target if available
    effect(() => {
      const destinationSelector = this.destinationSelector()

      if (destinationSelector?.nodeId) {
        const nodeId = parseInt(destinationSelector.nodeId.toString(), 10)

        // Only set if the form is still at its initial state (null)
        if (!this.destinationForm.get('nodeId')?.value) {
          this.destinationForm.get('nodeId')?.setValue(nodeId)
        }
      }
    })
  }

  async onImplementClick(): Promise<void> {
    const { nodeId, actionId, businessRuleIndex } = this.destinationForm.getRawValue()
    const node = this.nodes().find((node) => node.id === +nodeId)

    if (this.isBusinessRuleDestinationRequired()) {
      const result: { actionId?: number } = {}

      await this.#appNavigationService.execute(
        {
          id: NavigationActions.showBrArea,
          data: {
            nodeId,
            appId: this.appId,
            appVersionUuid: this.appVersionId,
            processVersionUuid: this.selectedProcessResourceId,
            processId: this.selectedProcessResourceId,
            businessRuleIndex,
            actionId,
          },
        },
        result
      )
      await new Promise((resolve) => setTimeout(resolve, 500))
      const targetActionId = actionId > -1 ? actionId : result.actionId
      const action = this.actions().find((action) => action.id === targetActionId)
      const ruleIndex =
        businessRuleIndex !== -1 ? businessRuleIndex : this.businessRules().length - 1
      this.confirmDestination(
        JSON.stringify({
          nodeId,
          actionId: targetActionId,
          index: ruleIndex,
          name: `${node.name}, ${action.name}, Rule ${ruleIndex + 1}`,
        }),
        {
          name: `${node.name}, ${action.name}, Rule ${ruleIndex + 1}`,
          targetType: ConversationThreadExecutionPlanActionTargetType.BusinessRule,
          businessRule: {
            nodeType: TargetNodeType.ServiceTask,
            nodeId: nodeId.toString(),
            actionId: targetActionId.toString(),
            index: ruleIndex,
          },
        }
      )

      return
    }

    if (this.destinationSelector()?.agent === AgentName.AiDesigner) {
      await this.#navigateToNode(node)

      this.confirmDestination(
        JSON.stringify({
          userTaskNavigationAreaId: node.navigationAreaId,
          name: node?.name ?? '',
        }),
        {
          name: node?.name ?? '',
          targetType: ConversationThreadExecutionPlanActionTargetType.Node,
          node: {
            id: nodeId.toString(),
            nodeType: node.type,
            navigationAreaId: node.navigationAreaId.toString(),
          },
        }
      )

      return
    }

    if (this.isExpressionDestinationRequired()) {
      await this.#appNavigationService.execute({
        id:
          this.destinationForm.get('expressionType')?.value ===
          ConversationThreadExecutionPlanActionTargetExpressionType.Hide
            ? NavigationActions.showHideExpressionArea
            : NavigationActions.showDisableExpressionArea,
        data: {
          nodeId,
          appId: this.appId,
          appVersionUuid: this.appVersionId,
          processVersionUuid: this.selectedProcessResourceId,
          processId: this.selectedProcessResourceId,
          swimlaneId: node.swimlaneDefinitionId,
          navigationAreaId: node.navigationAreaId,
          showHideExpression:
            this.destinationForm.get('expressionType')?.value ===
            ConversationThreadExecutionPlanActionTargetExpressionType.Hide,
          showDisableExpression:
            this.destinationForm.get('expressionType')?.value ===
            ConversationThreadExecutionPlanActionTargetExpressionType.Disable,
          templateIds: this.destinationForm.getRawValue()?.templateConfigId,
        },
      })

      this.confirmDestination(
        JSON.stringify({
          userTaskNavigationAreaId: node.navigationAreaId,
          templateIds: this.destinationForm.getRawValue()?.templateConfigId,
          expressionType: this.destinationForm.get('expressionType')?.value,
          name: `${node.name}, ${
            this.templateConfigs().find(
              (config) => config.id === this.destinationForm.getRawValue()?.templateConfigId
            )?.label
          }, ${this.destinationForm.get('expressionType')?.value}`,
        }),
        {
          name: `${node.name}, ${
            this.templateConfigs().find(
              (config) => config.id === this.destinationForm.getRawValue()?.templateConfigId
            )?.label
          }, ${this.destinationForm.get('expressionType')?.value}`,
          targetType: ConversationThreadExecutionPlanActionTargetType.Expression,
          expression: {
            nodeId: nodeId.toString(),
            elementId: this.destinationForm.getRawValue()?.templateConfigId.toString(),
            expressionType: this.destinationForm.get('expressionType')?.value,
          },
        }
      )
    }
  }

  confirmDestination(
    uiTarget: string,
    actionTarget: ActionTarget
  ): void {
    this.#store.dispatch(
      ExecutionPlanActions.runExecutionPlanConfirmation({
        actionId: this.destinationSelector()?.actionId,
        target: actionTarget,
        context: {
          items: [
            {
              key: ContextDataKeys.UiTarget,
              itemType: ConversationThreadContextDataItemType.GenericString,
              valueGenericString: uiTarget,
            },
          ],
        },
      })
    )
  }

  onCancelClick(): void {
    this.#store.dispatch(ConversationActions.discardThreadClick({}))
    this.#aiExecutionPlanService
      .patchExecutionPlanAction(this.destinationSelector()?.actionId, {
        state: ConversationThreadExecutionPlanActionState.Pending,
      })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe()
  }

  #navigateToNode(node: NodeDefinition): void {
    if (!node) {
      return
    }

    this.#appNavigationService.execute({
      id: NavigationActions.openNode,
      data: {
        nodeId: node.id,
        swimlaneId: node.swimlaneDefinitionId,
        navigationAreaId: node.navigationAreaId,
        appId: this.appId,
        appVersionUuid: this.appVersionId,
        processVersionUuid: this.selectedProcessResourceId,
        processId: this.selectedProcessResourceId,
      },
    })
  }
}
